/**
 * Listing photo duplicate detection — SSOT for content fingerprints and slot uniqueness.
 *
 * Hash is computed from **final stored bytes** (post listing compression). Upload paths
 * must hash the same blob written to Storage before persisting `item_images.image_hash`.
 *
 * @see docs/project-management/2-in-progress (2-max)/0-p0/listing-photo-duplicate-detection/
 */

/** Minimum listing photos required before uniqueness is meaningful. */
export const LISTING_PHOTO_SLOT_COUNT = 3;

/** Stable API / client error code (Phase 2 sell flows). */
export const DUPLICATE_LISTING_PHOTOS_ERROR_CODE =
  "duplicate_listing_photos" as const;

export class ListingPhotoDuplicateError extends Error {
  readonly code = DUPLICATE_LISTING_PHOTOS_ERROR_CODE;

  constructor(message: string) {
    super(message);
    this.name = "ListingPhotoDuplicateError";
  }
}

export function isListingPhotoDuplicateError(
  error: unknown,
): error is ListingPhotoDuplicateError {
  return (
    error instanceof ListingPhotoDuplicateError ||
    (error instanceof Error &&
      "code" in error &&
      (error as { code?: string }).code === DUPLICATE_LISTING_PHOTOS_ERROR_CODE)
  );
}

export type ListingPhotoUniquenessMode = "exact";

export type ListingPhotoUniquenessResult = {
  pass: boolean;
  mode: ListingPhotoUniquenessMode;
  /** Slot indices (0-based) involved in at least one duplicate pair. */
  duplicateSlotIndices: number[];
  /** Groups of slots sharing the same hash (size ≥ 2). */
  duplicateGroups: number[][];
  /** True when any slot hash is null/empty — legacy or not yet fingerprinted. */
  unknownLegacy: boolean;
  detail: string | null;
};

export type EvaluateListingPhotoUniquenessOptions = {
  /**
   * When true (D8 default), null hashes do not fail — caller treats as "unknown".
   * Phase 3 auto-approve may set false for stricter enforcement.
   */
  legacyNullPasses?: boolean;
  /** Expected slot count for a complete listing (default 3). */
  expectedSlotCount?: number;
};

const DEFAULT_UNIQUENESS_OPTIONS: Required<EvaluateListingPhotoUniquenessOptions> =
  {
    legacyNullPasses: true,
    expectedSlotCount: LISTING_PHOTO_SLOT_COUNT,
  };

function normalizeHash(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

/**
 * Short prefix stored in `item_images.image_signature` (hex chars from SHA-256).
 */
export function buildListingImageSignature(contentHash: string): string {
  return contentHash.slice(0, 16);
}

/**
 * SHA-256 hex digest of raw image bytes (Node or Web Crypto).
 */
export async function computeListingImageContentHash(
  data: ArrayBuffer | Uint8Array,
): Promise<string> {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  // Own ArrayBuffer copy — satisfies Web Crypto BufferSource typing (no SharedArrayBuffer).
  const digestSource = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(digestSource).set(bytes);

  if (
    typeof globalThis !== "undefined" &&
    globalThis.crypto &&
    typeof globalThis.crypto.subtle?.digest === "function"
  ) {
    const hashBuffer = await globalThis.crypto.subtle.digest(
      "SHA-256",
      digestSource,
    );
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  const { createHash } = await import("node:crypto");
  return createHash("sha256").update(bytes).digest("hex");
}

/**
 * Hash a Blob/File (browser upload path).
 */
export async function computeListingImageContentHashFromBlob(
  blob: Blob,
): Promise<string> {
  const buffer = await blob.arrayBuffer();
  return computeListingImageContentHash(buffer);
}

/**
 * Pure evaluator: are all fingerprinted slots distinct (exact SHA-256)?
 */
export function evaluateListingPhotoUniqueness(
  hashes: readonly (string | null | undefined)[],
  options?: EvaluateListingPhotoUniquenessOptions,
): ListingPhotoUniquenessResult {
  const opts = { ...DEFAULT_UNIQUENESS_OPTIONS, ...options };
  const normalized = hashes.map(normalizeHash);
  const unknownLegacy = normalized.some((h) => h === null);

  if (unknownLegacy && opts.legacyNullPasses) {
    return {
      pass: true,
      mode: "exact",
      duplicateSlotIndices: [],
      duplicateGroups: [],
      unknownLegacy: true,
      detail: "Photo fingerprints pending",
    };
  }

  const duplicateGroups: number[][] = [];
  const hashToIndices = new Map<string, number[]>();

  normalized.forEach((hash, index) => {
    if (!hash) return;
    const group = hashToIndices.get(hash) ?? [];
    group.push(index);
    hashToIndices.set(hash, group);
  });

  for (const indices of hashToIndices.values()) {
    if (indices.length >= 2) {
      duplicateGroups.push([...indices].sort((a, b) => a - b));
    }
  }

  const duplicateSlotIndices = [...new Set(duplicateGroups.flat())].sort(
    (a, b) => a - b,
  );

  if (duplicateGroups.length > 0) {
    const slotLabels = duplicateSlotIndices.map((i) => i + 1).join(", ");
    return {
      pass: false,
      mode: "exact",
      duplicateSlotIndices,
      duplicateGroups,
      unknownLegacy,
      detail: `Photos ${slotLabels} look like the same image`,
    };
  }

  const knownCount = normalized.filter(Boolean).length;
  if (knownCount > 0 && knownCount < opts.expectedSlotCount && unknownLegacy) {
    if (opts.legacyNullPasses) {
      return {
        pass: true,
        mode: "exact",
        duplicateSlotIndices: [],
        duplicateGroups: [],
        unknownLegacy: true,
        detail: "Photo fingerprints incomplete",
      };
    }
    return {
      pass: false,
      mode: "exact",
      duplicateSlotIndices: [],
      duplicateGroups: [],
      unknownLegacy: true,
      detail: "Photo fingerprints incomplete",
    };
  }

  if (unknownLegacy && !opts.legacyNullPasses) {
    return {
      pass: false,
      mode: "exact",
      duplicateSlotIndices: [],
      duplicateGroups: [],
      unknownLegacy: true,
      detail: "Photo fingerprints pending",
    };
  }

  return {
    pass: true,
    mode: "exact",
    duplicateSlotIndices: [],
    duplicateGroups: [],
    unknownLegacy,
    detail: null,
  };
}

/** Unique stand-ins so null legacy slots do not collide in duplicate detection. */
function listingPhotoHashesWithPendingPlaceholders(
  hashes: readonly (string | null | undefined)[],
): string[] {
  return hashes.map(
    (hash, index) => normalizeHash(hash) ?? `__pending_slot_${index}__`,
  );
}

/**
 * Enforce distinct photos for sell create (Phase 2). All slots must be
 * fingerprinted; throws on duplicate or missing hash.
 */
export function assertListingPhotoUniqueness(
  hashes: readonly (string | null | undefined)[],
  options?: EvaluateListingPhotoUniquenessOptions,
): ListingPhotoUniquenessResult {
  const result = evaluateListingPhotoUniqueness(hashes, {
    legacyNullPasses: false,
    ...options,
  });
  if (!result.pass) {
    throw new ListingPhotoDuplicateError(
      listingPhotoDuplicateBlockMessage(result),
    );
  }
  return result;
}

/**
 * Edit flow: detect duplicates among known hashes; legacy null slots are ignored
 * until re-uploaded (no backfill required).
 */
export function assertListingPhotoUniquenessAmongSlots(
  hashes: readonly (string | null | undefined)[],
): ListingPhotoUniquenessResult {
  const result = evaluateListingPhotoUniqueness(
    listingPhotoHashesWithPendingPlaceholders(hashes),
    { legacyNullPasses: false },
  );
  if (!result.pass) {
    throw new ListingPhotoDuplicateError(
      listingPhotoDuplicateBlockMessage(result),
    );
  }
  return result;
}

/** Sort DB/client images by slot position and collect content hashes. */
export function listingImageContentHashesFromImages(
  images: ReadonlyArray<{
    position?: number | null;
    /** Client / domain `ItemImage` field. */
    hash?: string | null;
    /** DB `item_images.image_hash` column (auto-approve / service selects). */
    image_hash?: string | null;
  }>,
): (string | null)[] {
  return [...images]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((img) => {
      const raw = img.hash ?? img.image_hash;
      if (typeof raw !== "string") return null;
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    });
}

export type SellListingPhotoHashValidationResult =
  | { ok: true; hashes: string[] }
  | {
      ok: false;
      code: typeof DUPLICATE_LISTING_PHOTOS_ERROR_CODE;
      message: string;
    };

/**
 * API + sell create pre-check: exactly three non-empty SHA-256 hex hashes, all distinct.
 */
export function validateSellListingPhotoContentHashesForApi(
  hashes: unknown,
): SellListingPhotoHashValidationResult {
  if (!Array.isArray(hashes) || hashes.length !== LISTING_PHOTO_SLOT_COUNT) {
    return {
      ok: false,
      code: DUPLICATE_LISTING_PHOTOS_ERROR_CODE,
      message: "Exactly three photo fingerprints are required.",
    };
  }

  const normalized = hashes.map((value) =>
    typeof value === "string" ? value.trim() : "",
  );

  if (normalized.some((hash) => hash.length === 0)) {
    return {
      ok: false,
      code: DUPLICATE_LISTING_PHOTOS_ERROR_CODE,
      message: "Could not fingerprint all photos. Try uploading again.",
    };
  }

  const uniqueness = evaluateListingPhotoUniqueness(normalized, {
    legacyNullPasses: false,
  });

  if (!uniqueness.pass) {
    return {
      ok: false,
      code: DUPLICATE_LISTING_PHOTOS_ERROR_CODE,
      message: listingPhotoDuplicateBlockMessage(uniqueness),
    };
  }

  return { ok: true, hashes: normalized };
}

/** Seller-facing message when create/edit should block (Phase 2). */
export function listingPhotoDuplicateBlockMessage(
  result: Pick<ListingPhotoUniquenessResult, "duplicateSlotIndices">,
): string {
  if (result.duplicateSlotIndices.length === 0) {
    return "Use three different photos of the item.";
  }
  const slots = result.duplicateSlotIndices.map((i) => i + 1);
  if (slots.length === 2) {
    return `Photo ${slots[0]} looks the same as Photo ${slots[1]}. Use three different angles.`;
  }
  return "Two or more photos look the same. Use three different angles.";
}
